import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ButtonGroup, Spinner} from '@blueprintjs/core';

import {deleteBlock} from '../../../../store/actions/blockActions';
import {addBlock} from '../../../../store/actions/reportFormActions';
import {toggleSavedBlockDialog} from '../../../../store/actions/uiActions';
import {getBlockSelector} from '../../../../store/reducers/blockReducer';
import {fromBlockToContent} from '../../utils';
import {usePreviewBlock} from '../../hooks';
import ContentToolbarActions from './ContentToolbarActions';
import SelectConnection from './SelectConnection';
import {ExecuteButton, PreviewButton} from './Buttons';
import {useIsVisible} from '../ContentCardContext';

import './ContentToolbar.scss';

type Callback = (value?: any) => void;

interface ContentToolbarProps {
  contentId: string | number;
  show?: boolean;
  selected?: string;
  options?: any[];
  onSelect?: Callback;
  showSelect?: boolean;
  selectText?: string; // Helper text shown beside the select
  selectLabel?: string; // Helper text shown inside Select to select an option
  executing?: boolean;
  onExecute?: Callback | null;
  onPreview?: Callback | null;
  onDelete?: Callback | null;
  updating?: boolean;
  showPreview?: boolean;
  showSave?: boolean;
  showDelete?: boolean;
  showDuplicate?: boolean;
  disabled?: boolean;
  summaryElement?: any;
}

export const ContentToolbar = ({
  contentId,
  show = true,
  selected,
  options,
  onSelect,
  selectText = undefined,
  selectLabel,
  executing = false,
  onExecute = null,
  onPreview = null,
  onDelete = undefined,
  updating = false,
  showPreview = false,
  showSelect = true,
  showDelete = true,
  showDuplicate = true,
  showSave = true,
  disabled = undefined,
}: ContentToolbarProps) => {
  const block = useSelector(getBlockSelector(contentId as string));
  const {
    isPreview,
    data: previewData,
    loading: loadingPreview,
    executePreview
  }: any = usePreviewBlock(contentId as string);
  const dispatch = useDispatch();

  /*
   * NOTE: buttons are not disabled on preview, you can still run or toggle preview mode.
   */
  const buttonsDisabled: boolean = (disabled !== undefined ? disabled : !selected);
  const isVisible = useIsVisible();

  useEffect(() => {
    /*
     * If there is no selected connection, select the first one
     */
    if (!options) {
      return;
    }

    if (contentId && !selected && options?.length > 0) {
      selectValue(options[0]);
    }
  }, [contentId]);

  useEffect(() => {
    if (!onPreview) {
      return;
    }

    if (isPreview) {
      onPreview(previewData);
    } else if (isPreview === false) {
      onPreview(previewData);
    }
  }, [isPreview]);

  const selectValue = (value: any) => {
    onSelect && onSelect(value.value);
  };

  const onSave = () => {
    dispatch(toggleSavedBlockDialog(true, contentId as string));
  };

  const _onDelete = () => {
    dispatch(deleteBlock(contentId as string, true));

    onDelete && onDelete(contentId as string);
  };

  const onDuplicate = () => {
    if (!block) {
      return;
    }

    const content: any = fromBlockToContent(block);
    /*
     * Force new content creation
     *
     * @TODO - similar code can be found on creating a block from a saved block
     */
    content.from_block_id = content.block_id;
    content.id = (new Date()).getTime();
    content.uid = undefined;
    content.block_id = undefined;
    content.name = ''; // set in the reducer

    dispatch(addBlock(content));
  };

  const _executePreview = () => {
    executePreview(/* block */);
  };

  if (!show) {
    return null;
  }

  return (
    <div className="content-toolbar">
      <ButtonGroup className="content-toolbar__left">
        <ExecuteButton
          show={!!onExecute}
          loading={executing}
          onClick={onExecute}
          disabled={buttonsDisabled}
          showLabel={isVisible}
        />
        <PreviewButton
          isPreview={isPreview}
          show={showPreview}
          loading={loadingPreview}
          onClick={_executePreview}
          disabled={buttonsDisabled}
          showLabel={isVisible}
        />
        {selectText && <div className="content-toolbar__select-text">{selectText}</div>}
        <SelectConnection
          show={showSelect}
          selected={selected || ''}
          onSelect={selectValue}
          options={options}
          label={selectLabel}
          disabled={disabled}
        />
      </ButtonGroup>
      <ButtonGroup className="content-toolbar__right">
        {updating && <Spinner size={16}/>}
        <ContentToolbarActions
          contentId={contentId}
          disabled={updating || disabled || false}
          showSave={showSave}
          showDelete={showDelete}
          showDuplicate={showDuplicate}
          onSave={onSave}
          onDuplicate={onDuplicate}
          onDelete={_onDelete}
        />
      </ButtonGroup>
    </div>
  );
};

export default ContentToolbar;
